(ns hai.admin-ui.usage
  "Usage views"
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.link :as link]
   [hai.ui.components.mahdi-table :as mt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [hai.web-app.platform :as platform]
   [lambdaisland.deja-fu :as fu]))

(defn- assistant-base-url [id]
  (some->> id (platform/url "/app/knowledge")))

(defn- assistant-chat-url [id]
  (some->> id (platform/url "/app/chats")))

(defn slack-channel-url [team-id channel-id]
  (when (and team-id channel-id)
    (str "https://app.slack.com/client/" team-id \/ channel-id)))

(defn- with-truncated-text
  [limit ^string s]
  {:pre [(pos? limit)]}
  [table/table-cell
   (if (some-> s count (> limit))
     [:span {:title s} (-> s (subs 0 limit) (str "..."))]
     s)])

(def with-text-200
  (partial with-truncated-text 200))

(def common-cols
  [{:header "Timestamp"
    :attr :created_at
    :render
    (fn [{:keys [created_at]}]
      [table/table-cell
       [:span {:title (str created_at)}
        (fu/distance-in-words (fu/local-date-time) (goog.date.DateTime. created_at))
        " ago"]])}
   {:header "User"
    :attr (comp :user/email :metadata)
    :render (fn [row]
              (let [email (get-in row [:metadata :user/email])]
                [table/table-cell
                 (if (str/blank? email)
                   [:span {:title "This response came from someone using your public assistant without logging in. No account is attached."}
                    "Anonymous session"]
                   email)]))
    :sort?  false}
   {:header "Input message"
    :attr   :input
    :sort?  false
    :render (fn [{:keys [type input]}]
              (if (or (str/starts-with? type "AirTable")
                      (str/starts-with? type "Notion"))
                (let [parts (str/split input #"\n")]
                  [table/table-cell
                   (if (< 1 (count parts))
                     (into [:span] (interpose [:br]) parts)
                     (first parts))])
                (with-text-200 input)))}

   {:header "Assistant response"
    :attr   :output
    :sort?  false
    :render (comp with-text-200 :output)}

   {:header "Type"
    :attr   :type
    :sort?  false
    :render (fn [{:keys [type]}] [table/table-cell type])}

   {:header "Origin"
    :attr   :origin
    :sort?  false
    :render (fn [{:keys [type origin metadata]}]
              (let [{:chat/keys  [uuid name]
                     :slack/keys [team channel channel-name]} metadata
                    origin (or (some->> channel-name (str "Slack #"))
                               origin)]
                [table/table-cell
                 (cond
                   (str/starts-with? origin "Slack")
                   (if-some [url (slack-channel-url team channel)]
                     [link/link {:href  url
                                 :target "_blank"}
                      origin]
                     origin)

                   (= type "AI response")
                   (if-some [url (cond
                                   (str/starts-with? origin "browser")
                                   nil
                                   ;; TODO: how about other origins - e.g. flows, extension?
                                   :else
                                   (assistant-chat-url uuid))]
                     [link/link {:href  url
                                 :target "_blank"}
                      origin]
                     origin)

                   (str/starts-with? type "CrunchBase")
                   (if-some [url (assistant-chat-url uuid)]
                     [link/link {:href   url
                                 :target "_blank"}
                      origin]
                     origin)

                   (or (str/starts-with? type "AirTable")
                       (str/starts-with? type "Notion"))
                   origin
                   :else "n/a")]))}

   {:header "Knowledge"
    :sort?  false
    :render (fn [{:keys [type origin metadata]}]
              (let [{:ai-model-config/keys [name uuid]} metadata]
                [table/table-cell
                 (cond
                   (= type "AI response")
                   (if (str/starts-with? origin "browser")
                     (if-some [url (:knowledge/site metadata)]
                       [link/link {:href   url
                                   :target "_blank"}
                        "Web-page"]
                       "Web-page")
                     (if-some [url (assistant-base-url uuid)]
                       [link/link {:href   url
                                   :target "_blank"}
                        name]
                       name))

                   (str/starts-with? type "CrunchBase")
                   "Crunchbase"

                   (str/starts-with? type "AirTable")
                   (if-some [url (get metadata :airtable/record-url)]
                     [link/link {:href   url
                                 :target "_blank"}
                      "Airtable record"]
                     "Airtable record")

                   (str/starts-with? type "Notion")
                   (if-some [url (get metadata :notion/page-url)]
                     [link/link {:href   url
                                 :target "_blank"}
                      "Notion page"]
                     "Notion page")
                   :else
                   "n/a")]))}])

(def no-events-found (constantly [:em "No events found"]))

(defn- personal-activity-table []
  [collection/component
   {:columns    (remove (comp #{"User"} :header) common-cols) ;; why show the user's own email here???
    ;; TODO default card render
    :card (fn [collection item]
            [card/card
             [cards/card-select-checkbox collection item]
             [cards/column-render collection item]])
    :query      ['(:goldtracker/usage-events
                   {:search-attrs [:input :output :type :origin]
                    :filters      {:goldtracker.event/own-only? true}})]
    :paginate?  true
    :no-items   no-events-found
    :pagination {:direction :desc :sort-attrs [:created_at]}
    :export-url "/events/v1?type=usage,own_only"}])

(defn- workspace-activity-table []
  [collection/component
   {:columns    common-cols
    ;; TODO default card render
    :card (fn [collection item]
            [card/card
             [cards/card-select-checkbox collection item]
             [cards/column-render collection item]])
    :query      ['(:goldtracker/usage-events
                   {:search-attrs ["metadata::json ->> 'user/email'"
                                   :input :output :type :origin]})]
    :paginate?  true
    :no-items   no-events-found
    :pagination {:direction :desc :sort-attrs [:created_at]}
    :export-url "/events/v1?type=usage"}])

(defn entity-link
  [entity-type {:datomic/keys [entity entity-original parent-entity]}]
  (let [entity-uuid  (get entity (keyword entity-type "uuid"))
        entity-title (or (get entity (keyword entity-type "title"))
                         (get entity-original (keyword entity-type "title"))
                         "unknown")
        url (case entity-type
              "ai-model-config" (assistant-base-url entity-uuid)
              "ai-source"       (:ai-source/link entity) ;; will be nil for local sources
              ;; For local-file sources, use the download endpoint; for others use citation-link
              "ai-file"         (or (not-empty (:ai-file/citation-link entity))
                                    (str "/assistant/v0/sources/" (:ai-source/uuid parent-entity) "/files/" entity-uuid "/download"))

              (println "Unsupported entity:" entity-type))]
    (if (nil? url)
      entity-title
      [link/link {:href   url
                  :target "_blank"}
       entity-title])))

(def ^goog.i18n.DateTimeFormat entity-datetime-formatter
  (goog.i18n.DateTimeFormat. "yyyy-MM-dd HH:mm"))

(defn- entity-activity-table
  "Single column table - i.e. poor man's timeline."
  []
  [collection/component
   {:columns    [{:header "Journal entry"
                  :render
                  (fn [{:keys [description_v2 entity_type created_at metadata]}]
                    [table/table-cell
                     [:span {:title (str created_at)}
                      (->> (goog.date.DateTime. created_at)
                           (.format entity-datetime-formatter))
                      \space
                      description_v2]
                     (entity-link entity_type metadata)])}]
    ;; TODO default card render
    :card (fn [collection item]
            [card/card
             [cards/card-select-checkbox collection item]
             [cards/column-render collection item]])
    :query      ['(:goldtracker/entity-events
                   {:search-attrs [:description_v2]})]
    :paginate?  true
    :no-items   no-events-found
    :pagination {:direction :desc :sort-attrs [:created_at]}
    ;:export-url "/events/v1?type=entity"
    }])

(defn- activity-tabs [route-name]
  (let [value (case route-name
                :usage/my-events  "personal"
                "workspace")]
    [:<>
     [c/SegmentedControlRoot
      {:value         value
       :onValueChange #(>e [:routing/navigate
                            (case %
                              "personal"  :usage/my-events
                              "workspace" :usage/events)])}
      [c/SegmentedControlItem {:value "personal"}  "Personal Activity"]
      [c/SegmentedControlItem {:value "workspace"} "Workspace Activity"]]
     (case value
       "personal"  [personal-activity-table]
       "workspace" [workspace-activity-table])]))

(defn activity-view
  [r]
  (let [route-name (-> @r :data :name)]
    [layout/page
     [layout/breadcrumbs]
     [layout/heading]
     [layout/lead
      "Personal activity is your interactions with Humble. Workspace activity is how your team uses what you've shared."] ;; see GG-1891
     [layout/content
      [activity-tabs route-name]]]))

(defn journal-view
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead
    "See how far you've come. Capture what matters along the way."] ;; see GG-1891
   [layout/content
    [entity-activity-table]]])

(defn user-count
  []
  [mt/remote-data-table :usage/active-users
   {:header "Domain"
    :render :domain}
   {:header "User count"
    :render :user_count}])

(defn routes
  []
  [["/journal"
    {:icon icons/outlined-book}
    ["/events"
     {:name :entity/my-events
      :label "Journal"
      :view journal-view
      ::routing/window-title "Journal"}]]

   ["/usage"
    {:icon icons/outlined-insights}
    ["/events"
     {:name        :usage/events
      :label       "Workspace Activity"
      :view        activity-view
      ::routing/window-title "Workspace Activity"}]
    ["/my-events"
     {:name        :usage/my-events
      :label       "Personal Activity"
      :breadcrumbs ["Personal Activity"]
      :view        activity-view
      ::routing/window-title "Personal Activity"}]]])
